import React, { useState, useEffect } from 'react';
import { userApi } from '../api';

function UserTable({ users, onEdit, onDelete }) {
  const [appStatuses, setAppStatuses] = useState({});
  const [loadingStatus, setLoadingStatus] = useState({});

  // Fetch app status for all users
  useEffect(() => {
    const fetchStatuses = async () => {
      if (users.length === 0) return;

      try {
        const userIds = users.map(u => u.id);
        const response = await userApi.getBatchAppStatus(userIds);
        setAppStatuses(response.data);
      } catch (error) {
        console.error('Error fetching batch app statuses:', error);
        // Set default values for all users on error
        const statuses = {};
        users.forEach(user => {
          statuses[user.id] = { found: false, active: null };
        });
        setAppStatuses(statuses);
      }
    };

    fetchStatuses();
  }, [users]);

  const handleToggleStatus = async (userId) => {
    setLoadingStatus(prev => ({ ...prev, [userId]: true }));
    try {
      const response = await userApi.toggleAppStatus(userId);
      setAppStatuses(prev => ({
        ...prev,
        [userId]: { ...prev[userId], active: response.data.active },
      }));
    } catch (error) {
      console.error('Error toggling app status:', error);
    } finally {
      setLoadingStatus(prev => ({ ...prev, [userId]: false }));
    }
  };

  if (users.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
        No users found
      </div>
    );
  }

  return (
    <div className="overflow-x-auto bg-white rounded-lg shadow">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Username
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              App Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {users.map((user) => {
            const status = appStatuses[user.id];
            const isLoading = loadingStatus[user.id];

            return (
              <tr key={user.id} className="hover:bg-gray-50 transition cursor-pointer">
                <td 
                  onClick={() => onEdit(user.id)}
                  className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600 hover:text-blue-900"
                >
                  {user.user}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {status ? (
                    status.found ? (
                      <button
                        onClick={() => handleToggleStatus(user.id)}
                        disabled={isLoading}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          status.active ? 'bg-green-600' : 'bg-gray-300'
                        } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            status.active ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        No app connected
                      </span>
                    )
                  ) : (
                    <span className="text-gray-500 text-xs">Loading...</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <button
                    onClick={() => onEdit(user.id)}
                    className="text-blue-600 hover:text-blue-900 transition"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => onDelete(user.id)}
                    className="text-red-600 hover:text-red-900 transition"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default UserTable;
